import { useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';
import { useEverythingYouNeedForADropdown } from './LabeledInputLib';
import { LabeledInputPresenter } from './LabeledInputPresenter';
import { DEFAULT_LABELED_SELECT_STYLES, LabeledSelectStyles } from './labeledSelectStyles';

export type LabeledSelectProps = {
  title?: string;
  label?: string;
  value: string;
  items?: { value: string; label?: string; displayLabel?: string }[];
  defaultValue?: string;
  defaultDisplayValue?: string;
  placeholder?: string;
  disabled?: boolean;
  style?: LabeledInputStyle;
  size?: LabeledInputSize;
  width?: string;
  onChange?: (value: string) => void;
  onUpdate?: (value: string) => void;
  backgroundColor?: string;
  delay?: number;
  allowAddNew?: boolean;
  emptyMessage?: string;
  optional?: boolean;
  tabIndex?: number;
  /** Recolors the field's frame (border + label) and its displayed value/placeholder text —
   *  e.g. a red to flag a required selection that's still missing. Leaves everything else
   *  (background, sizing) untouched. */
  frameColor?: string;
  /** Swappable presentation — see labeledSelectStyles.tsx. Defaults to the legacy look. */
  styles?: LabeledSelectStyles;
};

export const LabeledSelect = ({
  title,
  label,
  value: localValue,
  placeholder,
  defaultValue,
  defaultDisplayValue,
  items,
  disabled = false,
  style = 'normal',
  size = 'medium',
  width = '100%',
  delay = 500,
  onChange,
  onUpdate,
  backgroundColor,
  allowAddNew = false,
  emptyMessage = 'No options found',
  optional = false,
  tabIndex,
  frameColor,
  styles = DEFAULT_LABELED_SELECT_STYLES,
}: LabeledSelectProps) => {
  const { dropdown, dropdownPositionStyling, containerRef, dropdownRef, isPicklistOpen, setIsPicklistOpen, forceUpdate } = useEverythingYouNeedForADropdown();
  const isTiny = size === 'tiny';
  const [searchText, setSearchText] = useState('');
  const [selectedValue, setSelectedValue] = useState(localValue);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOpenBeforeMouseDownRef = useRef(false);

  useEffect(() => {
    forceUpdate();
  }, [localValue, forceUpdate]);

  // Update local selected value when prop changes
  useEffect(() => {
    setSelectedValue(localValue);
  }, [localValue]);

  const debouncedUpdate = useDebouncedCallback(
    (updatedValue: string) => onUpdate?.(updatedValue),
    delay,
  );

  const togglePicklist = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setIsPicklistOpen(!isPicklistOpen);
  };

  const handleOptionSelect = (optionValue: string) => {
    setSelectedValue(optionValue);
    onChange?.(optionValue);
    debouncedUpdate(optionValue);
    debouncedUpdate.flush();
    setIsPicklistOpen(false);
  };

  const handlePicklistKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isPicklistOpen) {
        setIsPicklistOpen(true);
      }
    } else if (event.key === 'Escape') {
      setIsPicklistOpen(false);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      togglePicklist();
    }
  };

  const selectedOption = useMemo(() => items?.find(opt => opt.value === selectedValue), [items, selectedValue]);
  const defaultOption = useMemo(() => items?.find(opt => opt.value === defaultValue), [items, defaultValue]);
  const pickListDisplayValue = selectedOption
    ? selectedOption?.displayLabel ?? selectedOption?.label ?? selectedOption?.value
    : defaultDisplayValue || defaultOption?.label || placeholder || '';

  const filteredItems = useMemo(
    () =>
      !searchText
        ? items
        : items?.filter(opt => (opt.displayLabel || opt.label || opt.value)?.toString().toLowerCase().includes(searchText.toLowerCase())),
    [items, searchText],
  );

  useEffect(() => {
    if (isPicklistOpen) searchInputRef.current?.focus();
    else setSearchText('');
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, [isPicklistOpen]);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const first = filteredItems?.[0] || (allowAddNew ? { value: searchText } : undefined);
      if (first) handleOptionSelect(first.value);
    } else if (event.key === 'Escape') {
      setIsPicklistOpen(false);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement | HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && (containerRef.current?.contains(relatedTarget) || dropdownRef.current?.contains(relatedTarget))) {
      return;
    }
    // Delay closing to allow click events on dropdown to fire first
    blurTimeoutRef.current = setTimeout(() => {
      setIsPicklistOpen(false);
    }, 150);
  };

  const handleInputMouseDown = () => {
    wasOpenBeforeMouseDownRef.current = isPicklistOpen;
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (wasOpenBeforeMouseDownRef.current && !searchText) {
      searchInputRef.current?.blur();
    }
  };

  const handleDropdownMouseDown = (event: React.MouseEvent) => {
    // Cancel blur timeout if clicking on dropdown
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    event.preventDefault();
  };

  return (
    <div ref={containerRef} style={{ width }}>
      <LabeledInputPresenter
        width={width}
        style={style}
        size={size}
        backgroundColor={backgroundColor}
        onClick={disabled ? () => {} : togglePicklist}
        title={title || ''}
        isFocused={isPicklistOpen}
        label={styles.showFloatingLabel ? label : undefined}
        optional={optional}
        disabled={disabled}
        frameColor={frameColor}
      >
        {isPicklistOpen && !disabled
          ? (
            <input
              ref={searchInputRef}
              css={[
                styles.pickListInlineInput,
                styles.inputSizes[size],
                styles.valueTypographySizes[size],
                disabled && styles.pickListInlineInputDisabled,
              ]}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              onMouseDown={handleInputMouseDown}
              onClick={handleInputClick}
              onBlur={handleBlur}
            />
          )
          : (
            <div
              css={[
                styles.pickListText,
                styles.inputSizes[size],
                styles.valueTypographySizes[size],
                !selectedValue && styles.pickListTextPlaceholder,
                frameColor && styles.renderFrameColorText(frameColor),
                disabled && styles.pickListInlineInputDisabled,
              ]}
              onKeyDown={handlePicklistKeyDown}
              onBlur={handleBlur}
              tabIndex={disabled ? -1 : (tabIndex ?? 0)}
            >
              {selectedValue && isTiny && label && `${label}: `}
              {pickListDisplayValue}
            </div>
          )}
        {!disabled && (
          <div css={styles.expandIconContainer} onClick={disabled ? undefined : togglePicklist}>
            {styles.renderExpandIcon(isPicklistOpen)}
          </div>
        )}
      </LabeledInputPresenter>
      {dropdown(() => (
        <div
          ref={dropdownRef}
          css={[
            styles.pickListDropdown,
            size === 'tiny' && styles.pickListDropdownTiny,
            dropdownPositionStyling,
          ]}
          onMouseDown={handleDropdownMouseDown}
        >
          {filteredItems && filteredItems.length > 0
            ? (
              filteredItems.map(option => (
                <div
                  key={option.value?.toString()}
                  css={[
                    styles.pickListOption,
                    styles.valueTypographySizes[size],
                    option.value === selectedValue && styles.pickListOptionSelected,
                  ]}
                  onClick={e => {
                    e.stopPropagation();
                    handleOptionSelect(option.value);
                  }}
                >
                  {styles.renderOptionMarker ? styles.renderOptionMarker?.(option.value === selectedValue) : null}
                  {option.label || option.value}
                </div>
              ))
            )
            : (
              <div
                css={[
                  styles.pickListOption,
                  styles.valueTypographySizes[size],
                  styles.pickListEmptyMessage,
                ]}
              >
                {emptyMessage}
              </div>
            )}
        </div>
      ))}
    </div>
  );
};

type FormikLabeledInputPicklistProps = Omit<LabeledSelectProps, 'value' | 'onChange' | 'onUpdate'> & {
  name: string;
};

export const FormikLabeledSelect = (labeledInputPicklistProps: FormikLabeledInputPicklistProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  return (
    <LabeledSelect
      {...labeledInputPicklistProps}
      value={values[labeledInputPicklistProps.name] || ''}
      onChange={value => setFieldValue(labeledInputPicklistProps.name, value)}
    />
  );
};
